import type { Brand, BrandTokens, DeepPartial, GameEntry, OperatorSettings, SlotOptions, SlotOverrides } from '../types';

/**
 * Operator layer: turns back-office settings for a catalog game into `createSlot` options.
 * Everything here is pure data — the same settings can live in a platform database, be encoded
 * into a launch URL, or be stored in localStorage (showcase).
 */

export function defaultOperatorSettings(gameId: string): OperatorSettings {
  return {
    version: 1,
    gameId,
    math: null,
    brand: null,
    jurisdiction: 'default',
    locale: 'en',
    currency: {},
    demoBalance: 1000,
    bets: { list: null, defaultBet: null },
    maxWinMultiplier: null,
    features: {
      quickSpin: null,
      quickStop: null,
      autoplay: null,
      bonusBuy: null,
      celebrateBelowStake: null,
      sessionClock: null,
      netPosition: null,
      realityCheckMinutes: null,
      sound: null,
    },
    branding: { tokens: {}, logo: null, title: null, hudStyle: null, hudPosition: null, spinShape: null, spinStyle: null, spinArt: null, strings: {} },
  };
}

/** Deep-merge a partial settings object over the defaults (validates shape loosely). */
export function normalizeSettings(gameId: string, partial: DeepPartial<OperatorSettings> | null | undefined): OperatorSettings {
  const d = defaultOperatorSettings(gameId);
  if (!partial || typeof partial !== 'object') return d;
  const p = partial as Partial<OperatorSettings>;
  return {
    ...d,
    ...p,
    gameId,
    version: 1,
    currency: { ...d.currency, ...(p.currency ?? {}) },
    bets: { ...d.bets, ...(p.bets ?? {}) },
    features: { ...d.features, ...(p.features ?? {}) },
    branding: {
      ...d.branding,
      ...(p.branding ?? {}),
      tokens: { ...((p.branding?.tokens ?? {}) as BrandTokens) },
      strings: { ...((p.branding?.strings ?? {}) as Record<string, string>) },
    },
  };
}

/** Operator game title rule: a string, 1–40 characters after trim; anything else is rejected (null). */
export function validGameTitle(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const v = value.trim();
  return v.length >= 1 && v.length <= 40 ? v : null;
}

function hasKeys(o: object): boolean {
  return Object.keys(o).length > 0;
}

/** Build `SlotOverrides` from the settings (only the parts that differ from "default"). */
export function toOverrides(s: OperatorSettings): SlotOverrides {
  const ov: SlotOverrides = {};
  const f = s.features;

  const math: NonNullable<SlotOverrides['math']> = {};
  if (s.bets.list && s.bets.list.length) math.bets = [...s.bets.list].sort((a, b) => a - b);
  if (s.bets.defaultBet != null) math.defaultBet = s.bets.defaultBet;
  if (s.maxWinMultiplier != null) math.maxWinMultiplier = s.maxWinMultiplier;
  if (f.bonusBuy != null) math.bonusBuy = { enabled: f.bonusBuy };
  if (hasKeys(math)) ov.math = math;

  const j: NonNullable<SlotOverrides['jurisdiction']> = {};
  if (f.quickSpin != null) j.allowTurbo = f.quickSpin;
  if (f.quickStop != null) j.allowQuickStop = f.quickStop;
  if (f.autoplay != null) j.allowAutoplay = f.autoplay;
  if (f.bonusBuy != null) j.allowBonusBuy = f.bonusBuy;
  if (f.celebrateBelowStake != null) j.celebrateBelowStake = f.celebrateBelowStake;
  if (f.sessionClock != null) j.showSessionClock = f.sessionClock;
  if (f.netPosition != null) j.showNetPosition = f.netPosition;
  if (f.realityCheckMinutes != null) j.realityCheckMinutes = f.realityCheckMinutes > 0 ? f.realityCheckMinutes : null;
  if (hasKeys(j)) ov.jurisdiction = j;


  const b = s.branding;
  const brand: DeepPartial<Brand> = {};
  if (hasKeys(b.tokens)) brand.tokens = { ...b.tokens };
  if (b.logo) brand.logo = { src: b.logo, position: 'top-left', height: 32 };
  const title = validGameTitle(b.title);
  if (title) brand.title = title;
  if (b.hudStyle || b.hudPosition) brand.hud = { ...(b.hudStyle ? { style: b.hudStyle } : {}), ...(b.hudPosition ? { position: b.hudPosition } : {}) };
  if (b.spinShape || b.spinStyle || b.spinArt != null)
    brand.buttons = { ...(b.spinShape ? { spinShape: b.spinShape } : {}), ...(b.spinStyle ? { style: b.spinStyle } : {}), ...(b.spinArt != null ? { spinArt: b.spinArt } : {}) };
  if (hasKeys(b.strings)) brand.strings = { ...b.strings };
  if (hasKeys(brand)) ov.brand = brand;
  return ov;
}

/** `createSlot` options for a catalog entry configured by an operator. */
export function toSlotOptions(entry: GameEntry, s: OperatorSettings, extra: Partial<SlotOptions> = {}): SlotOptions {
  const ov = toOverrides(s);
  const brandName = s.brand ?? entry.brand ?? null;
  // Branding overrides need a brand to attach to; fall back to an inline neutral brand.
  const brand: SlotOptions['brand'] = brandName ?? (ov.brand ? ({ id: 'operator', ...ov.brand } as Brand) : undefined);
  return {
    theme: entry.theme,
    math: s.math ?? entry.math ?? 'default',
    brand,
    jurisdiction: s.jurisdiction,
    locale: s.locale,
    currency: hasKeys(s.currency) ? s.currency : undefined,
    balance: s.demoBalance,
    overrides: brandName ? ov : { ...ov, brand: undefined },
    ...extra,
  };
}

// ---------------------------------------------------------------- persistence & launch URLs

export function settingsStorageKey(gameId: string): string {
  return `slot-operator:${gameId}`;
}

export function loadSettings(gameId: string): OperatorSettings {
  try {
    const raw = globalThis.localStorage?.getItem(settingsStorageKey(gameId));
    return normalizeSettings(gameId, raw ? (JSON.parse(raw) as Partial<OperatorSettings>) : null);
  } catch {
    return defaultOperatorSettings(gameId);
  }
}

export function saveSettings(s: OperatorSettings): void {
  try {
    globalThis.localStorage?.setItem(settingsStorageKey(s.gameId), JSON.stringify(s));
  } catch {
    /* storage unavailable */
  }
}

export function clearSettings(gameId: string): void {
  try {
    globalThis.localStorage?.removeItem(settingsStorageKey(gameId));
  } catch {
    /* ignore */
  }
}

/** base64url(JSON) for launch URLs. */
export function encodeSettings(s: OperatorSettings): string {
  const json = JSON.stringify(s);
  const bytes = new TextEncoder().encode(json);
  let bin = '';
  for (const b of bytes) bin += String.fromCharCode(b);
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

export function decodeSettings(gameId: string, encoded: string | null | undefined): OperatorSettings | null {
  if (!encoded) return null;
  try {
    const b64 = encoded.replace(/-/g, '+').replace(/_/g, '/') + '='.repeat((4 - (encoded.length % 4)) % 4);
    const bin = atob(b64);
    const bytes = Uint8Array.from(bin, (c) => c.charCodeAt(0));
    const parsed = JSON.parse(new TextDecoder().decode(bytes)) as Partial<OperatorSettings>;
    return normalizeSettings(gameId, parsed);
  } catch {
    return null;
  }
}

/** Launch URL a platform would open in an iframe: `<base>game.html?game=<id>&config=<encoded settings>`. */
export function launchUrl(base: string, s: OperatorSettings): string {
  const url = new URL('game.html', base);
  url.searchParams.set('game', s.gameId);
  url.searchParams.set('config', encodeSettings(s));
  return url.href;
}
